import React, { useState } from 'react';
import {
    Box,
    Button,
    Chip,
    Drawer,
    IconButton,
    LinearProgress,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    alpha,
    useTheme,
} from '@mui/material';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { AdoptionAssignment, AdoptionTeacher, EmptyNote, Panel, TeacherAdoption } from './schoolDashboard';

/**
 * TEACHER ADOPTION -- who is setting work, and how it is landing.
 *
 * A teacher counts as active with at least one published assignment in the
 * window. Tests built but never set are shown beside it, not counted: building
 * a test is effort, setting it is adoption.
 *
 * Completion and average score are over CLOSED assignments only (the API
 * decides that), so a teacher whose test is still open is not penalised for
 * it. Clicking a row opens the teacher's assignments for the window.
 */

/** A teacher with no work set for this long is flagged even if still "active". */
const STALE_DAYS = 7;

const daysSince = (iso: string | null) => {
    if (!iso) return null;
    const t = new Date(iso.replace(' ', 'T')).getTime();
    if (isNaN(t)) return null;
    return Math.max(0, Math.floor((Date.now() - t) / 86400000));
};

const agoLabel = (days: number | null) =>
    days === null ? 'Never' : days === 0 ? 'Today' : days === 1 ? 'Yesterday' : `${days} days ago`;

const shortDate = (iso: string | null) => {
    if (!iso) return '—';
    const d = new Date(iso.replace(' ', 'T'));
    return isNaN(d.getTime())
        ? iso
        : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

const pctText = (v: number | null | undefined) => (v === null || v === undefined ? '—' : `${Math.round(v)}%`);

/** Colour for a completion rate: the same 70/50 split as the risk bands. */
const rateColor = (v: number | null | undefined) =>
    v === null || v === undefined ? 'text.secondary' : v >= 70 ? 'success.main' : v >= 50 ? 'warning.main' : 'error.main';

const STATE_CHIP: Record<AdoptionAssignment['state'], { label: string; color: 'default' | 'info' | 'success' }> = {
    closed: { label: 'Closed', color: 'default' },
    open: { label: 'Open now', color: 'success' },
    upcoming: { label: 'Upcoming', color: 'info' },
};

const Fact = ({ label, value, color }: { label: string; value: React.ReactNode; color?: string }) => (
    <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, flex: '1 1 110px', minWidth: 110 }}>
        <Typography variant="caption" color="text.secondary">
            {label}
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3, color }}>
            {value}
        </Typography>
    </Paper>
);

const AssignmentCard = ({ a }: { a: AdoptionAssignment }) => {
    const chip = STATE_CHIP[a.state];
    const rate = a.assigned > 0 ? (a.submitted / a.assigned) * 100 : null;
    const when =
        a.state === 'upcoming'
            ? `Opens ${shortDate(a.scheduled_start)}`
            : a.state === 'open'
            ? `Closes ${shortDate(a.scheduled_end)}`
            : `Closed ${shortDate(a.scheduled_end)}`;

    return (
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {a.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {[a.subject, a.sections].filter(Boolean).join(' · ') || 'No sections'} · Set{' '}
                        {shortDate(a.published_at)} · {when}
                    </Typography>
                </Box>
                <Chip size="small" color={chip.color} label={chip.label} sx={{ flexShrink: 0 }} />
            </Stack>

            {a.state !== 'upcoming' && (
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1.25 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" justifyContent="space-between">
                            <Typography variant="caption" color="text.secondary">
                                {a.submitted} of {a.assigned} submitted
                                {a.missed > 0 ? ` · ${a.missed} missed` : ''}
                            </Typography>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: rateColor(rate) }}>
                                {pctText(rate)}
                            </Typography>
                        </Stack>
                        <LinearProgress
                            variant="determinate"
                            value={rate ?? 0}
                            color={rate === null ? 'inherit' : rate >= 70 ? 'success' : rate >= 50 ? 'warning' : 'error'}
                            sx={{ height: 6, borderRadius: 3, mt: 0.5 }}
                        />
                    </Box>
                    <Box sx={{ textAlign: 'right', flexShrink: 0, width: 70 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Avg score
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {pctText(a.average_pct)}
                        </Typography>
                    </Box>
                </Stack>
            )}
        </Paper>
    );
};

const TeacherDrawer = ({
    teacher,
    windowDays,
    onClose,
}: {
    teacher: AdoptionTeacher | null;
    windowDays: number;
    onClose: () => void;
}) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const list = teacher?.assignments || [];
    const sections = teacher?.sections || [];
    const since = daysSince(teacher?.last_published_at ?? null);

    return (
        <Drawer
            anchor="right"
            open={teacher !== null}
            onClose={onClose}
            PaperProps={{ sx: { width: { xs: '100%', sm: 560 }, maxWidth: '100%' } }}
        >
            {teacher && (
                <>
                    <Box sx={{ p: 2.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
                        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                    {teacher.full_name || `Teacher ${teacher.user_id}`}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {sections.length > 0
                                        ? `Teaches ${sections.join(', ')}`
                                        : 'Not assigned to any section'}
                                </Typography>
                            </Box>
                            <IconButton onClick={onClose} aria-label="Close">
                                <CloseIcon />
                            </IconButton>
                        </Stack>

                        <Stack direction="row" sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
                            <Fact label={`Set, last ${windowDays} days`} value={teacher.assignments_published} />
                            <Fact label="Tests built" value={teacher.tests_created} />
                            <Fact
                                label="Completion"
                                value={pctText(teacher.completion_pct)}
                                color={rateColor(teacher.completion_pct)}
                            />
                            <Fact label="Avg score" value={pctText(teacher.average_pct)} />
                            <Fact
                                label="Last set"
                                value={agoLabel(since)}
                                color={since === null || since > STALE_DAYS ? 'warning.main' : undefined}
                            />
                        </Stack>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            Completion and average score count closed assignments only.
                        </Typography>
                    </Box>

                    <Box sx={{ p: 2.5, overflowY: 'auto' }}>
                        {list.length === 0 ? (
                            <Stack spacing={1.5} alignItems="flex-start">
                                <EmptyNote>
                                    {sections.length === 0
                                        ? `No work set in the last ${windowDays} days. This teacher is not assigned to any section, so there is no class to set work for. Assign them to a section first.`
                                        : `No work set in the last ${windowDays} days for ${sections.join(', ')}. Those students are getting assigned work from other teachers only.`}
                                </EmptyNote>
                                <Button onClick={() => navigate('/school-teacher')} endIcon={<ChevronRightIcon />}>
                                    Open teachers
                                </Button>
                            </Stack>
                        ) : (
                            <Stack spacing={1.25}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                    Assignments set, newest first
                                </Typography>
                                {list.map(a => (
                                    <AssignmentCard key={a.assignment_id} a={a} />
                                ))}
                            </Stack>
                        )}
                    </Box>
                </>
            )}
        </Drawer>
    );
};

export const TeacherAdoptionPanel = ({ adoption }: { adoption?: TeacherAdoption }) => {
    const theme = useTheme();
    const [openId, setOpenId] = useState<number | null>(null);
    if (!adoption) return null;

    const { teachers_active, teachers_total, window_days, teachers } = adoption;
    const inactive = teachers_total - teachers_active;
    const testsBuilt = adoption.tests_created ?? teachers.reduce((s, t) => s + t.tests_created, 0);
    const maxSet = Math.max(1, ...teachers.map(t => t.assignments_published));
    const open = teachers.find(t => t.user_id === openId) || null;

    return (
        <Panel
            title="Teacher adoption"
            tone="secondary"
            icon={<AssignmentTurnedInIcon fontSize="small" />}
            subtitle={`Who is setting work, last ${window_days} days, and how it is landing. Click a teacher for their assignments.`}
            action={
                teachers_total > 0 ? (
                    <Tooltip title="Teachers who published at least one assignment">
                        <Chip
                            size="small"
                            color={teachers_active === teachers_total ? 'success' : 'warning'}
                            label={`${teachers_active} of ${teachers_total} active`}
                            sx={{ fontWeight: 700 }}
                        />
                    </Tooltip>
                ) : undefined
            }
        >
            {teachers_total === 0 ? (
                <EmptyNote>No teachers on the roster yet.</EmptyNote>
            ) : (
                <>
                    <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
                        <Chip size="small" variant="outlined" label={`${adoption.assignments_published} assignments set`} />
                        <Chip size="small" variant="outlined" label={`${testsBuilt} tests built`} />
                        {inactive > 0 && (
                            <Chip
                                size="small"
                                color="warning"
                                variant="outlined"
                                label={`${inactive} not setting work`}
                            />
                        )}
                    </Stack>

                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>Teacher</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Set</TableCell>
                                <TableCell sx={{ fontWeight: 700 }} align="right">
                                    <Tooltip title="Submitted out of assigned, closed assignments only">
                                        <span>Completion</span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700 }} align="right">
                                    Avg score
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Last set</TableCell>
                                <TableCell padding="none" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {teachers.map(t => {
                                const since = daysSince(t.last_published_at);
                                const stale = since === null || since > STALE_DAYS;
                                const idle = t.assignments_published === 0;
                                return (
                                    <TableRow
                                        key={t.user_id}
                                        hover
                                        onClick={() => setOpenId(t.user_id)}
                                        sx={{
                                            cursor: 'pointer',
                                            bgcolor: idle ? alpha(theme.palette.warning.main, 0.06) : undefined,
                                            '&:last-child td': { borderBottom: 0 },
                                        }}
                                    >
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {t.full_name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {t.sections && t.sections.length > 0
                                                    ? t.sections.join(', ')
                                                    : 'No sections'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ minWidth: 90 }}>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontWeight: 700,
                                                        width: 16,
                                                        color: idle ? 'warning.main' : undefined,
                                                    }}
                                                >
                                                    {t.assignments_published}
                                                </Typography>
                                                <Box
                                                    sx={{
                                                        flex: 1,
                                                        height: 6,
                                                        borderRadius: 3,
                                                        bgcolor: alpha(theme.palette.secondary.main, 0.12),
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: `${(t.assignments_published / maxSet) * 100}%`,
                                                            height: '100%',
                                                            borderRadius: 3,
                                                            bgcolor: 'secondary.main',
                                                        }}
                                                    />
                                                </Box>
                                            </Stack>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography
                                                variant="body2"
                                                sx={{ fontWeight: 600, color: rateColor(t.completion_pct) }}
                                            >
                                                {pctText(t.completion_pct)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2">{pctText(t.average_pct)}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title={t.last_published_at ? t.last_published_at.slice(0, 10) : ''}>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        fontWeight: stale ? 700 : 400,
                                                        color: stale ? 'warning.main' : 'text.secondary',
                                                    }}
                                                >
                                                    {agoLabel(since)}
                                                </Typography>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell padding="none" sx={{ width: 24 }}>
                                            <ChevronRightIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </>
            )}

            <TeacherDrawer teacher={open} windowDays={window_days} onClose={() => setOpenId(null)} />
        </Panel>
    );
};

export default TeacherAdoptionPanel;
